import React, { useEffect, useMemo, useState } from 'react';
import { listSessions } from '../services/api';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  Clock3,
  Search,
  User,
  Phone,
} from 'lucide-react';

const PAGE_SIZE = 10;

const Sessions = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const formatDate = (value) =>
    value ? new Date(value).toLocaleString('pt-BR') : null;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await listSessions();
        setRows(data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((s) => {
      const nome = String(s.cliente_nome || '').toLowerCase();
      const tel = String(s.cliente_telefone || '').toLowerCase();
      return nome.includes(term) || tel.includes(term);
    });
  }, [rows, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageEnd = pageStart + PAGE_SIZE;
  const paginated = filtered.slice(pageStart, pageEnd);

  // Reseta página ao buscar
  useEffect(() => {
    setPage(1);
  }, [search]);

  const stats = useMemo(() => {
    const total = rows.length;
    const done = rows.filter((s) => s.finalizado_em).length;
    return { total, done, pending: total - done };
  }, [rows]);

  // Janela de páginas a exibir nos controles
  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const result = [1];
    const startWindow = Math.max(2, safePage - 1);
    const endWindow = Math.min(totalPages - 1, safePage + 1);
    if (startWindow > 2) result.push('…');
    for (let i = startWindow; i <= endWindow; i += 1) result.push(i);
    if (endWindow < totalPages - 1) result.push('…');
    result.push(totalPages);
    return result;
  }, [totalPages, safePage]);

  const getInitials = (name) => {
    if (!name) return null;
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return null;
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  return (
    <div className="px-2 sm:px-6 pb-12 sm:pb-16">
      <div
        className="max-w-[1280px] mx-auto rounded-2xl sm:rounded-3xl border p-3 sm:p-5 md:p-6 overflow-hidden space-y-5"
        style={{
          borderColor: 'rgba(15, 23, 42, 0.06)',
          background:
            'radial-gradient(900px circle at 10% -20%, rgba(251, 236, 214, 0.55) 0%, transparent 55%), radial-gradient(1000px circle at 90% 0%, rgba(214, 234, 248, 0.55) 0%, transparent 60%), #F6F4F1',
        }}
      >
      {/* Hero header */}
      <div className="flex flex-col items-center text-center gap-1.5 px-2 pt-2">
        <div className="flex items-center gap-2">
          <ClipboardList size={22} className="text-[#1F5C3A]" />
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">
            Respostas de feedback
          </h2>
        </div>
        <p className="text-sm text-slate-500 max-w-xl leading-relaxed">
          Identifique quem respondeu, quando e o status de cada sessão do formulário.
        </p>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-200/70 bg-white px-4 py-3 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
            Total
          </div>
          <div className="text-2xl font-bold text-slate-800 mt-0.5">{stats.total}</div>
        </div>
        <div className="rounded-xl border border-[#2E8B57]/20 bg-[#EAF7EE]/40 px-4 py-3 shadow-[0_2px_8px_rgba(46,139,87,0.06)]">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[#1F5C3A]">
            Finalizadas
          </div>
          <div className="text-2xl font-bold text-[#1F5C3A] mt-0.5">{stats.done}</div>
        </div>
        <div className="rounded-xl border border-[#D7A64A]/30 bg-[#FFF4E4]/50 px-4 py-3 shadow-[0_2px_8px_rgba(215,166,74,0.06)]">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[#7B4B0C]">
            Em andamento
          </div>
          <div className="text-2xl font-bold text-[#7B4B0C] mt-0.5">{stats.pending}</div>
        </div>
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search
          size={16}
          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
        />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nome ou telefone…"
          className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2E8B57]/30 focus:border-[#2E8B57] transition shadow-[0_2px_8px_rgba(15,23,42,0.03)]"
        />
      </div>

      {/* Loading / Empty */}
      {loading && (
        <div className="rounded-2xl border border-slate-200/70 bg-white p-6 text-slate-500 text-sm">
          Carregando…
        </div>
      )}
      {!loading && rows.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 p-8 text-center">
          <ClipboardList size={32} className="text-slate-400 mx-auto mb-2" />
          <p className="text-sm font-medium text-slate-700">Nenhuma resposta ainda</p>
          <p className="text-xs text-slate-500 mt-1">
            Assim que clientes responderem o formulário, as sessões aparecerão aqui.
          </p>
        </div>
      )}
      {!loading && rows.length > 0 && filtered.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 p-6 text-center">
          <Search size={24} className="text-slate-400 mx-auto mb-2" />
          <p className="text-sm font-medium text-slate-700">
            Nenhuma sessão encontrada para “{search}”
          </p>
        </div>
      )}

      {/* Table (desktop) / Cards (mobile) */}
      {!loading && filtered.length > 0 && (
        <>
          {/* Desktop: table */}
          <div className="hidden lg:block rounded-2xl border border-slate-200/70 bg-white overflow-x-auto shadow-[0_4px_16px_rgba(15,23,42,0.04)]">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="text-left border-b border-slate-200/70 bg-slate-50/60">
                  <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Cliente
                  </th>
                  <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Início
                  </th>
                  <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Fim
                  </th>
                  <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Ação
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((s) => {
                  const isDone = !!s.finalizado_em;
                  const inicio = formatDate(s.iniciado_em);
                  const fim = formatDate(s.finalizado_em);
                  const initials = getInitials(s.cliente_nome);
                  const naoIdentificado = !s.cliente_nome;

                  return (
                    <tr
                      key={s.id}
                      className="group border-t border-slate-100 hover:bg-[#EAF7EE]/25 transition cursor-pointer"
                      onClick={() => navigate(`/feedback-hero/respostas/${s.id}`)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div
                            className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold border ${
                              naoIdentificado
                                ? 'bg-slate-100 text-slate-500 border-slate-200'
                                : isDone
                                  ? 'bg-[#EAF7EE] text-[#1F5C3A] border-[#2E8B57]/20'
                                  : 'bg-[#FFF4E4] text-[#7B4B0C] border-[#D7A64A]/30'
                            }`}
                          >
                            {initials || <User size={16} />}
                          </div>
                          <div className="min-w-0">
                            <div
                              className={`font-semibold ${
                                naoIdentificado ? 'text-slate-500 italic' : 'text-slate-800'
                              }`}
                            >
                              {s.cliente_nome || 'Cliente não identificado'}
                            </div>
                            <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                              <Phone size={11} />
                              {s.cliente_telefone || '—'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                        {inicio || '—'}
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                        {fim || <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wide border ${
                            isDone
                              ? 'border-[#2E8B57]/30 text-[#1F5C3A] bg-[#EAF7EE]'
                              : 'border-[#D7A64A]/40 text-[#7B4B0C] bg-[#FFF4E4]'
                          }`}
                        >
                          {isDone ? <CheckCircle2 size={11} /> : <Clock3 size={11} />}
                          {isDone ? 'Finalizada' : 'Em andamento'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/feedback-hero/respostas/${s.id}`);
                          }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 opacity-70 group-hover:opacity-100 group-hover:border-[#2E8B57]/30 group-hover:text-[#1F5C3A] group-hover:bg-[#EAF7EE] transition"
                        >
                          Abrir
                          <ChevronRight size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile/Tablet: cards */}
          <div className="lg:hidden space-y-2.5">
            {paginated.map((s) => {
              const isDone = !!s.finalizado_em;
              const inicio = formatDate(s.iniciado_em);
              const fim = formatDate(s.finalizado_em);
              const initials = getInitials(s.cliente_nome);
              const naoIdentificado = !s.cliente_nome;
              return (
                <button
                  key={s.id}
                  onClick={() => navigate(`/feedback-hero/respostas/${s.id}`)}
                  className="w-full text-left rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_2px_8px_rgba(15,23,42,0.04)] hover:border-[#2E8B57]/30 hover:shadow-[0_8px_24px_rgba(15,23,42,0.06)] transition"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                      <div
                        className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold border flex-shrink-0 ${
                          naoIdentificado
                            ? 'bg-slate-100 text-slate-500 border-slate-200'
                            : isDone
                              ? 'bg-[#EAF7EE] text-[#1F5C3A] border-[#2E8B57]/20'
                              : 'bg-[#FFF4E4] text-[#7B4B0C] border-[#D7A64A]/30'
                        }`}
                      >
                        {initials || <User size={16} />}
                      </div>
                      <div className="min-w-0">
                        <div
                          className={`font-semibold text-sm truncate ${
                            naoIdentificado ? 'text-slate-500 italic' : 'text-slate-800'
                          }`}
                        >
                          {s.cliente_nome || 'Cliente não identificado'}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {s.cliente_telefone || '—'}
                        </div>
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide border flex-shrink-0 ${
                        isDone
                          ? 'border-[#2E8B57]/30 text-[#1F5C3A] bg-[#EAF7EE]'
                          : 'border-[#D7A64A]/40 text-[#7B4B0C] bg-[#FFF4E4]'
                      }`}
                    >
                      {isDone ? <CheckCircle2 size={10} /> : <Clock3 size={10} />}
                      {isDone ? 'Finalizada' : 'Andamento'}
                    </span>
                  </div>
                  <div className="mt-2.5 pt-2.5 border-t border-slate-100 grid grid-cols-2 gap-2 text-[11px]">
                    <div>
                      <div className="text-slate-400 font-medium uppercase tracking-wider text-[10px]">
                        Início
                      </div>
                      <div className="text-slate-700">{inicio || '—'}</div>
                    </div>
                    <div>
                      <div className="text-slate-400 font-medium uppercase tracking-wider text-[10px]">
                        Fim
                      </div>
                      <div className="text-slate-700">{fim || '—'}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
              <p className="text-xs text-slate-500 order-2 sm:order-1">
                Mostrando{' '}
                <span className="font-bold text-slate-700">
                  {pageStart + 1}–{Math.min(pageEnd, filtered.length)}
                </span>{' '}
                de <span className="font-bold text-slate-700">{filtered.length}</span>{' '}
                {filtered.length === 1 ? 'sessão' : 'sessões'}
              </p>

              <nav
                className="inline-flex items-center gap-1 order-1 sm:order-2"
                aria-label="Paginação"
              >
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  aria-label="Página anterior"
                  className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-slate-300 hover:text-slate-900 transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={14} />
                </button>

                {pageNumbers.map((p, idx) =>
                  p === '…' ? (
                    <span
                      key={`ellipsis-${idx}`}
                      className="px-1 text-slate-400 text-xs select-none"
                    >
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPage(p)}
                      aria-current={p === safePage ? 'page' : undefined}
                      className={`inline-flex items-center justify-center min-w-[2rem] h-8 px-2 rounded-lg text-xs font-semibold transition ${
                        p === safePage
                          ? 'bg-[#2E8B57] text-white shadow-[0_4px_12px_rgba(46,139,87,0.22)]'
                          : 'border border-slate-200 bg-white text-slate-600 hover:bg-[#EAF7EE] hover:border-[#2E8B57]/30 hover:text-[#1F5C3A]'
                      }`}
                    >
                      {p}
                    </button>
                  ),
                )}

                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  aria-label="Próxima página"
                  className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-slate-300 hover:text-slate-900 transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={14} />
                </button>
              </nav>
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
};

export default Sessions;
